import type {
  CheckoutAdvancedFlowResponse,
  CoreConfiguration,
  PaymentAction,
  PaymentData,
} from '@adyen/adyen-web';

import type { CardFormReply } from '../domain/purchase';
import type { CardPaymentData } from './CardForm';

/** What the composition root knows about the payment account. */
export interface AdyenCardFormConfig {
  /** `test_…` or `live_…`; the prefix decides the environment. */
  clientKey: string;
  /** ISO country of the storefront; the SDK refuses to start without it. */
  countryCode: string;
  locale: string;
}

/** Locale of the card fields when the UI language has no Adyen translation. */
export const ADYEN_FALLBACK_LOCALE = 'en-US';

/** Adyen card-field locales by storefront UI language. */
const ADYEN_LOCALES: Record<string, string> = {
  ar: 'ar',
  de: 'de-DE',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  it: 'it-IT',
  nl: 'nl-NL',
  pl: 'pl-PL',
  pt: 'pt-PT',
  ru: 'ru-RU',
};

/** Card-field locale of a UI language (`en`, `ar`, `en-GB`…); English when Adyen has no translation. */
export const adyenLocaleFor = (lang: string): string =>
  ADYEN_LOCALES[lang.toLowerCase().split(/[-_]/)[0] ?? ''] ??
  ADYEN_FALLBACK_LOCALE;

/**
 * Country of the storefront for the SDK. A company without one cannot take
 * card payments, so the form fails to start (reported as an init failure)
 * instead of running under a country that is not the storefront's.
 */
export const adyenCountryCode = (countryCode: string | undefined): string => {
  if (!countryCode) {
    throw new Error('Membership card form: company countryCode is not set');
  }
  return countryCode;
};

/** Full-width 3DS challenge, so it fits the checkout column on a phone. */
export const CHALLENGE_WINDOW_SIZE = '05';

/** Environment matching the client key; a mismatch makes `AdyenCheckout` throw on init. */
export const adyenEnvironmentFor = (clientKey: string): 'live' | 'test' =>
  clientKey.startsWith('live_') ? 'live' : 'test';

/** Core options of the membership card form: no pay button, the store drives submit. */
export const adyenCoreOptions = (
  config: AdyenCardFormConfig,
): Pick<
  CoreConfiguration,
  'clientKey' | 'environment' | 'countryCode' | 'locale' | 'showPayButton'
> => ({
  clientKey: config.clientKey,
  environment: adyenEnvironmentFor(config.clientKey),
  countryCode: config.countryCode,
  locale: config.locale,
  showPayButton: false,
});

/** Card data for the backend; `origin` is sent by the Card but missing from its type. */
export const toCardPaymentData = (
  data: PaymentData,
  fallbackOrigin: string,
): CardPaymentData => {
  const { origin } = data as PaymentData & { origin?: unknown };
  return {
    paymentMethod: data.paymentMethod,
    browserInfo: data.browserInfo ? { ...data.browserInfo } : null,
    origin: typeof origin === 'string' && origin ? origin : fallbackOrigin,
  };
};

/**
 * Adyen's answer to a form callback, or `null` to reject. An action is
 * handed back so the SDK shows the 3DS challenge (or performs the redirect)
 * in place of the card fields; anything else ends the SDK's part — the store
 * owns the outcome, so the response never carries backend data.
 */
export const toAdyenResponse = (
  reply: CardFormReply,
): CheckoutAdvancedFlowResponse | null => {
  switch (reply.kind) {
    case 'action':
      return {
        resultCode:
          reply.action.type === 'redirect'
            ? 'RedirectShopper'
            : 'ChallengeShopper',
        action: reply.action as unknown as PaymentAction,
      };
    case 'done':
      return { resultCode: 'Authorised' };
    default:
      return null;
  }
};

/** The `actions` the SDK hands to `onSubmit` and `onAdditionalDetails`; one of them must be called. */
export interface AdyenCallbackActions {
  resolve: (response: CheckoutAdvancedFlowResponse) => void;
  reject: () => void;
}

/** The card form as `answerAdyen` sees it. */
export interface AdyenAnsweringForm {
  isDestroyed(): boolean;
  /** Takes the Card out of "loading" after a rejected callback it should not wait on. */
  setReady(): void;
}

/**
 * Answers an SDK callback once the store replied: an action or a settled
 * purchase resolves, a failure rejects, and an ignored call (a second submit
 * while one is in flight) rejects and puts the Card back to "ready". A reply
 * that threw counts as a failure; a form destroyed meanwhile answers nothing.
 */
export const answerAdyen = async (
  pending: Promise<CardFormReply>,
  actions: AdyenCallbackActions,
  form: AdyenAnsweringForm,
): Promise<void> => {
  const reply = await pending.catch((): CardFormReply => ({ kind: 'failed' }));
  if (form.isDestroyed()) {
    return;
  }
  const response = toAdyenResponse(reply);
  if (response) {
    actions.resolve(response);
    return;
  }
  actions.reject();
  if (reply.kind === 'ignored') {
    form.setReady();
  }
};
